import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Download, Save, RefreshCw, AlertCircle, Loader2,
  Search, Clock, Users, ChefHat, Palette, Image as ImageIcon,
  Eye, CheckCircle, BookOpen, Settings, Sparkles, Upload,
  Copy, Tag
} from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { supabase } from '../../lib/supabase';
import { wordpressService } from '../../services/wordpressService';

// ─── PDF HTML BUILDER ────────────────────────────────────────────────────────

function buildPdfHtml({
  title, coverImage, mainImage, ingredientsImage, instructionsImage,
  brandName, authorName, footerText, thankYouMessage,
  ingredients, instructions, notes, prepTime, cookTime, servings, calories,
  primaryColor, accentColor, fontStyle, showNotes, showPageNumbers,
  useFeaturedImage, insertImages, imageLayout, pageFormat, recipeTips,
}) {
  const fontFamily = 'Inter, -apple-system, Arial, sans-serif';

  const pw = pageFormat === 'A4' ? '210mm' : '216mm';
  const ph = pageFormat === 'A4' ? '297mm' : '279mm';
  const STEPS_PER_PAGE = 3;

  const safeInstructions = Array.isArray(instructions) ? instructions : [];
  const safeIngredients  = Array.isArray(ingredients)  ? ingredients  : [];

  const instructionChunks = [];
  for (let i = 0; i < safeInstructions.length; i += STEPS_PER_PAGE) {
    instructionChunks.push(safeInstructions.slice(i, i + STEPS_PER_PAGE));
  }

  // Uploaded images override the default article image per section
  const coverImgSrc = mainImage || coverImage;
  const ingImgSrc   = ingredientsImage || coverImage;
  const instImgSrc  = instructionsImage || coverImage;

  const showCoverImg = mainImage        ? true : (useFeaturedImage && !!coverImage);
  const showIngImg   = ingredientsImage ? true : (insertImages     && !!coverImage);
  const showInstImg  = instructionsImage ? true : (insertImages    && !!coverImage);
  const hasNotes       = showNotes && notes && notes.trim();

  // ── Tips data (no static fallbacks) ─────────────────────────────────────
  const tipSections      = recipeTips || {};
  const helpfulTips      = Array.isArray(tipSections.helpfulTips)      ? tipSections.helpfulTips      : [];
  const substitutions    = Array.isArray(tipSections.substitutions)    ? tipSections.substitutions    : [];
  const storageTips      = Array.isArray(tipSections.storageTips)      ? tipSections.storageTips      : [];
  const servingSuggestions = Array.isArray(tipSections.servingSuggestions) ? tipSections.servingSuggestions : [];
  const hasTips = helpfulTips.length > 0 || substitutions.length > 0 || storageTips.length > 0 || servingSuggestions.length > 0;

  // Use 2 tips pages when total content is large
  const totalTipChars = [...helpfulTips, ...substitutions, ...storageTips, ...servingSuggestions].join('').length;
  const totalTipItems = helpfulTips.length + substitutions.length + storageTips.length + servingSuggestions.length;
  const tipsNeedTwoPages = hasTips && (totalTipItems > 10 || totalTipChars > 700);
  const tipsPageCount = hasTips ? (tipsNeedTwoPages ? 2 : 1) : 0;

  // Total page count for page-number display
  const totalPages =
    1 +                                  // cover
    1 +                                  // ingredients
    Math.max(1, instructionChunks.length) + // instructions (at least 1)
    tipsPageCount +                      // tips (0, 1, or 2 pages)
    (hasNotes ? 1 : 0) +
    1;                                   // thank you

  const footer = (label) =>
    `<div class="page-footer">${label || brandName || 'Recipe'}</div>`;

  const pn = (n) =>
    showPageNumbers ? `<div class="page-number">${n} / ${totalPages}</div>` : '';

  const pages = [];

  // ── PAGE 1: Cover ──────────────────────────────────────────────────────────
  pages.push(`
    <div class="page cover-page">
      <div class="cover-hero-wrap">
        ${showCoverImg
          ? `<img src="${coverImgSrc}" class="cover-hero" alt="${title}" onerror="this.parentNode.style.background='linear-gradient(135deg,#f5e6c8,#c8956c)'" />`
          : `<div class="cover-hero-placeholder"></div>`}
        <div class="cover-gradient-overlay"></div>
        <div class="cover-stars-row">★★★★★</div>
      </div>
      <div class="cover-title-box">
        <h1 class="cover-title">${title}</h1>
        ${(authorName || brandName) ? `<p class="cover-byline">${authorName || brandName}</p>` : ''}
      </div>
      ${footer(footerText || brandName)}
    </div>`);

  // ── PAGE 2: Ingredients ────────────────────────────────────────────────────
  const ingListHtml = safeIngredients.map(ing => `<li>${ing}</li>`).join('');
  pages.push(`
    <div class="page ing-page">
      <div class="ing-content-col ing-full">
        <div class="ing-inner">
          <h2 class="sec-heading">Ingredients</h2>
          <ul class="ing-list">${ingListHtml || '<li>No ingredients detected</li>'}</ul>
          <div class="info-box">
            <div class="info-row">
              <div class="info-cell">
                <span class="info-icon">🍳</span>
                <div class="info-label">Prep Time</div>
                <div class="info-val">${prepTime || '—'}</div>
              </div>
              <div class="info-divider"></div>
              <div class="info-cell">
                <span class="info-icon">⏱</span>
                <div class="info-label">Cook / Bake</div>
                <div class="info-val">${cookTime || '—'}</div>
              </div>
              <div class="info-divider"></div>
              <div class="info-cell">
                <span class="info-icon">🍽</span>
                <div class="info-label">Servings</div>
                <div class="info-val">${servings || '—'}</div>
              </div>
              ${calories ? `
              <div class="info-divider"></div>
              <div class="info-cell">
                <span class="info-icon">🔥</span>
                <div class="info-label">Calories</div>
                <div class="info-val">${calories}</div>
              </div>` : ''}
            </div>
          </div>
        </div>
      </div>
      ${footer(footerText || brandName)}
      ${pn(2)}
    </div>`);

  // ── INSTRUCTION PAGES ──────────────────────────────────────────────────────
  // First instruction page gets image on top half (if available)
  const chunksToRender = instructionChunks.length > 0 ? instructionChunks : [[]];
  chunksToRender.forEach((chunk, ci) => {
    // Resolve which image (if any) belongs on this page
    const pageImgSrc = (ci === 0 && showIngImg)  ? ingImgSrc
                     : (ci === 1 && showInstImg) ? instImgSrc
                     : null;

    const stepsHtml = chunk.map((step, si) => {
      const n = ci * STEPS_PER_PAGE + si + 1;
      return `
        <div class="step-item">
          <div class="step-num">${n}</div>
          <div class="step-text">${step}</div>
        </div>`;
    }).join('');
    const emptyNote = chunk.length === 0
      ? `<p class="empty-note">No instructions detected — please enter manually.</p>` : '';

    let pageHtml;

    if (ci === 0 && pageImgSrc) {
      // Page 1: vertical image left, text right (mirrors the Ingredients page layout)
      pageHtml = `
      <div class="page inst-page-side">
        <div class="inst-img-side">
          <img src="${pageImgSrc}" class="inst-img" alt="${title}"
               onerror="this.parentNode.style.display='none'" />
        </div>
        <div class="inst-content-side">
          <h2 class="sec-heading">Instructions</h2>
          <div class="steps-list">${stepsHtml}${emptyNote}</div>
        </div>
        ${footer(footerText || brandName)}
        ${pn(ci + 3)}
      </div>`;
    } else if (ci === 1 && pageImgSrc) {
      // Page 2: text at top, image at bottom
      pageHtml = `
      <div class="page inst-page">
        <div class="inst-body-top">
          <h2 class="sec-heading">Instructions</h2>
          <div class="steps-list">${stepsHtml}${emptyNote}</div>
        </div>
        <div class="inst-img-band-bottom">
          <img src="${pageImgSrc}" class="inst-img" alt="${title}"
               onerror="this.parentNode.style.display='none'" />
        </div>
        ${footer(footerText || brandName)}
        ${pn(ci + 3)}
      </div>`;
    } else {
      // Page 3+: text only, full height
      pageHtml = `
      <div class="page inst-page">
        <div class="inst-body-full">
          <h2 class="sec-heading">Instructions</h2>
          <div class="steps-list">${stepsHtml}${emptyNote}</div>
        </div>
        ${footer(footerText || brandName)}
        ${pn(ci + 3)}
      </div>`;
    }

    pages.push(pageHtml);
  });

  // ── TIPS PAGE(S) ───────────────────────────────────────────────────────────
  const renderTipsList = (items) => items.map(t => `<li>${t}</li>`).join('');
  const tipsHeader = `
    <div class="tips-header">
      <h2 class="tips-title">Recipe Tips &amp; Extras</h2>
      <p class="tips-subtitle">${title}</p>
    </div>`;

  const tipsPageNum = Math.max(1, instructionChunks.length) + 3;

  if (hasTips) {

    if (tipsNeedTwoPages) {
      // ── Page A: Helpful Tips + Substitutions ─────────────────────────────
      pages.push(`
        <div class="page tips-page">
          ${tipsHeader}
          <div class="tips-grid">
            <div class="tips-cell tips-cell-right">
              <h3 class="tips-sec-title">💡 Helpful Tips</h3>
              <ul class="tips-list">${renderTipsList(helpfulTips)}</ul>
            </div>
            <div class="tips-cell">
              <h3 class="tips-sec-title">🔄 Substitutions</h3>
              <ul class="tips-list">${renderTipsList(substitutions)}</ul>
            </div>
          </div>
          ${footer(footerText || brandName)}
          ${pn(tipsPageNum)}
        </div>`);
      // ── Page B: Storage Tips + Serving Suggestions ───────────────────────
      pages.push(`
        <div class="page tips-page">
          ${tipsHeader}
          <div class="tips-grid">
            <div class="tips-cell tips-cell-right">
              <h3 class="tips-sec-title">📦 Storage Tips</h3>
              <ul class="tips-list">${renderTipsList(storageTips)}</ul>
            </div>
            <div class="tips-cell">
              <h3 class="tips-sec-title">🍽 Serving Suggestions</h3>
              <ul class="tips-list">${renderTipsList(servingSuggestions)}</ul>
            </div>
          </div>
          ${footer(footerText || brandName)}
          ${pn(tipsPageNum + 1)}
        </div>`);
    } else {
      // ── Single page: all 4 sections ──────────────────────────────────────
      pages.push(`
        <div class="page tips-page">
          ${tipsHeader}
          <div class="tips-grid">
            <div class="tips-cell tips-cell-right tips-cell-bottom">
              <h3 class="tips-sec-title">💡 Helpful Tips</h3>
              <ul class="tips-list">${renderTipsList(helpfulTips)}</ul>
            </div>
            <div class="tips-cell tips-cell-bottom">
              <h3 class="tips-sec-title">🔄 Substitutions</h3>
              <ul class="tips-list">${renderTipsList(substitutions)}</ul>
            </div>
            <div class="tips-cell tips-cell-right">
              <h3 class="tips-sec-title">📦 Storage Tips</h3>
              <ul class="tips-list">${renderTipsList(storageTips)}</ul>
            </div>
            <div class="tips-cell">
              <h3 class="tips-sec-title">🍽 Serving Suggestions</h3>
              <ul class="tips-list">${renderTipsList(servingSuggestions)}</ul>
            </div>
          </div>
          ${footer(footerText || brandName)}
          ${pn(tipsPageNum)}
        </div>`);
    }
  }

  // ── NOTES PAGE ─────────────────────────────────────────────────────────────
  if (hasNotes) {
    const notesPageNum = tipsPageNum + 1;
    const notesHtml = notes.split('\n').filter(l => l.trim()).map(l => `<p>${l}</p>`).join('');
    pages.push(`
      <div class="page notes-page">
        <div class="notes-inner">
          <h2 class="sec-heading">Notes</h2>
          <div class="notes-body">${notesHtml}</div>
        </div>
        ${footer(footerText || brandName)}
        ${pn(notesPageNum)}
      </div>`);
  }

  // ── FINAL PAGE: Thank You ──────────────────────────────────────────────────
  pages.push(`
    <div class="page final-page">
      <div class="final-accent-top"></div>
      <div class="final-inner">
        <div class="final-smiley">
          <svg viewBox="0 0 100 100" width="100" height="100">
            <circle cx="50" cy="50" r="48" fill="${primaryColor}22" stroke="${primaryColor}" stroke-width="3"/>
            <circle cx="35" cy="42" r="5" fill="${primaryColor}"/>
            <circle cx="65" cy="42" r="5" fill="${primaryColor}"/>
            <path d="M30 62 Q50 80 70 62" stroke="${primaryColor}" stroke-width="5" fill="none" stroke-linecap="round"/>
          </svg>
        </div>
        <h2 class="final-heading">Thank You!</h2>
        <p class="ty-text">${(thankYouMessage || 'Thank you so much for your support! Happy cooking!').replace(/\n/g, '<br/>')}</p>
        ${brandName ? `<p class="brand-sig">— ${brandName}</p>` : ''}
      </div>
      <div class="final-accent-bottom"></div>
      ${footer(footerText || brandName)}
    </div>`);

  // ── STYLES ─────────────────────────────────────────────────────────────────
  const css = `
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:${fontFamily};background:#fff;color:#1a1a1a;
         -webkit-print-color-adjust:exact;print-color-adjust:exact}
    @page{size:${pageFormat === 'A4' ? 'A4' : 'Letter'};margin:0}

    .page{
      width:${pw};height:${ph};
      position:relative;
      page-break-after:always;
      overflow:hidden;
      background:#fff;
    }
    .page:last-child{page-break-after:auto}

    /* ── COVER ────────────────────────────────── */
    .cover-page{display:flex;flex-direction:column}
    .cover-hero-wrap{
      position:relative;flex:1;overflow:hidden;
      background:linear-gradient(135deg,#f5e6c8,#c8956c);
    }
    .cover-hero{width:100%;height:100%;object-fit:cover;display:block}
    .cover-hero-placeholder{
      width:100%;height:100%;
      background:linear-gradient(135deg,#f5e6c8,#c8956c);
    }
    .cover-gradient-overlay{
      position:absolute;inset:0;z-index:1;
      background:linear-gradient(to bottom,transparent 50%,rgba(0,0,0,0.4));
    }
    .cover-stars-row{
      position:absolute;bottom:0;left:0;right:0;z-index:2;
      background:${accentColor}80;text-align:center;
      padding:10px 0;font-size:30pt;color:#FFD700;letter-spacing:10px;
    }
    .cover-title-box{flex-shrink:0;background:${primaryColor};padding:24px 36px 20px}
    .cover-title{
      color:#fff;font-size:36pt;text-align:center;
      font-weight:bold;line-height:1.2;
      text-shadow:0 2px 10px rgba(0,0,0,0.3);
    }
    .cover-byline{
      color:rgba(255,255,255,0.82);font-size:13pt;
      text-align:center;margin-top:8px;font-style:italic;
    }

    /* ── INGREDIENTS PAGE ─────────────────────── */
    .ing-page{display:flex;flex-direction:row;padding-bottom:42px}
    .ing-img-col{
      width:50%;flex-shrink:0;overflow:hidden;
      background:linear-gradient(160deg,#f5e6c8,#c8956c);
    }
    .ing-img{width:100%;height:100%;object-fit:cover;display:block}
    .ing-content-col{width:50%;display:flex;flex-direction:column}
    .ing-full{width:100%}
    .ing-inner{padding:16mm 14mm 8mm 14mm;height:100%;display:flex;flex-direction:column}
    .sec-heading{
      font-size:24pt;font-weight:bold;color:#1a1a1a;
      margin-bottom:14px;
      border-bottom:3px solid ${primaryColor};
      padding-bottom:6px;
    }
    .ing-list{list-style:none;padding:0;flex:1;overflow:hidden}
    .ing-list li{
      font-size:11.5pt;font-weight:600;line-height:1.7;
      padding:4px 0 4px 18px;
      border-bottom:1px solid #f0ead8;
      position:relative;
    }
    .ing-list li::before{
      content:"•";position:absolute;left:0;
      color:${primaryColor};font-size:15pt;line-height:1.4;
    }
    .info-box{
      background:${accentColor}22;border:1.5px solid ${accentColor}55;
      border-radius:10px;padding:10px 12px;margin-top:14px;flex-shrink:0;
    }
    .info-row{display:flex;align-items:stretch;gap:0}
    .info-cell{
      flex:1;display:flex;flex-direction:column;
      align-items:center;justify-content:center;
      padding:8px 6px;text-align:center;
    }
    .info-icon{font-size:18pt;margin-bottom:4px}
    .info-label{font-size:8pt;color:#888;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
    .info-val{font-size:13pt;font-weight:bold;color:#1a1a1a;margin-top:2px}
    .info-divider{width:1px;background:#d4c9a8;flex-shrink:0;margin:6px 0}

    /* ── INSTRUCTIONS ────────────────────────── */
    .inst-page{display:flex;flex-direction:column}
    .inst-page-side{display:flex;flex-direction:row;padding-bottom:42px}
    .inst-img-side{
      width:50%;flex-shrink:0;overflow:hidden;
      background:linear-gradient(160deg,#f5e6c8,#c8956c);
    }
    .inst-content-side{
      width:50%;padding:16mm 14mm 8mm 14mm;overflow:hidden;
    }
    .inst-img-band{
      height:45%;flex-shrink:0;overflow:hidden;
      background:linear-gradient(135deg,#f5e6c8,#c8956c);
    }
    .inst-img{width:100%;height:100%;object-fit:cover;display:block}
    .inst-body-half{
      flex:1;padding:10mm 18mm 14mm;overflow:hidden;
    }
    .inst-body-full{
      flex:1;padding:18mm 18mm 14mm;overflow:hidden;
    }
    .inst-body-top{
      padding:18mm 18mm 8mm;overflow:hidden;flex-shrink:0;
    }
    .inst-img-band-bottom{
      flex:1;overflow:hidden;
      background:linear-gradient(135deg,#f5e6c8,#c8956c);
      min-height:30%;
    }
    .steps-list{margin-top:10px}
    .step-item{
      display:flex;gap:16px;align-items:flex-start;
      margin-bottom:20px;
    }
    .step-num{
      width:36px;height:36px;border-radius:50%;
      background:${primaryColor};color:#fff;
      font-size:15pt;font-weight:bold;
      display:flex;align-items:center;justify-content:center;
      flex-shrink:0;margin-top:2px;
    }
    .step-text{font-size:13pt;font-weight:600;line-height:1.8;flex:1}
    .empty-note{font-size:11pt;color:#999;font-style:italic}

    /* ── TIPS PAGE ───────────────────────────── */
    .tips-page{display:flex;flex-direction:column}
    .tips-header{
      background:${primaryColor};
      padding:14mm 18mm 11mm;flex-shrink:0;
    }
    .tips-title{font-size:28pt;font-weight:bold;color:#fff;margin-bottom:5px}
    .tips-subtitle{font-size:13pt;color:rgba(255,255,255,0.8);font-style:italic}
    .tips-grid{
      display:grid;
      grid-template-columns:1fr 1fr;
      flex:1;
    }
    .tips-cell{
      padding:10mm 14mm;
      box-sizing:border-box;
    }
    .tips-cell-right{
      border-right:2px solid ${primaryColor}44;
    }
    .tips-cell-bottom{
      border-bottom:2px solid ${primaryColor}44;
    }
    .tips-sec-title{
      font-size:21pt;font-weight:bold;color:${primaryColor};
      text-align:center;
      margin-bottom:7mm;
      padding-bottom:4mm;
      border-bottom:1.5px solid ${primaryColor}33;
    }
    .tips-list{list-style:none;padding:0;margin:0}
    .tips-list li{
      font-size:15pt;line-height:1.85;
      padding:2px 0 2px 22px;
      position:relative;color:#333;
    }
    .tips-list li::before{
      content:"•";position:absolute;left:0;
      color:${primaryColor};font-weight:bold;font-size:17pt;
      line-height:1.5;
    }

    /* ── NOTES PAGE ──────────────────────────── */
    .notes-page{padding-bottom:42px}
    .notes-inner{padding:20mm 18mm 8mm}
    .notes-body{margin-top:12px;font-size:12pt;line-height:1.9}
    .notes-body p{margin-bottom:10px}

    /* ── FINAL PAGE ──────────────────────────── */
    .final-page{
      display:flex;flex-direction:column;
      background:linear-gradient(160deg,${accentColor}22,${accentColor}11);
    }
    .final-accent-top,.final-accent-bottom{height:10mm;background:${primaryColor};flex-shrink:0}
    .final-inner{
      flex:1;display:flex;flex-direction:column;
      align-items:center;justify-content:center;
      padding:16mm 28mm 12mm;text-align:center;
    }
    .final-smiley{margin-bottom:20px}
    .final-heading{font-size:38pt;font-weight:bold;color:${primaryColor};margin-bottom:18px}
    .ty-text{font-size:14pt;line-height:2;max-width:150mm;color:#444;margin-bottom:24px}
    .brand-sig{font-size:16pt;font-weight:bold;color:${primaryColor};text-decoration:underline}

    /* ── FOOTER ──────────────────────────────── */
    .page-footer{
      position:absolute;bottom:0;left:0;right:0;
      background:${primaryColor};text-align:center;
      padding:9px;color:#fff;font-size:12pt;font-weight:bold;font-style:italic;
    }
    .page-number{
      position:absolute;bottom:42px;right:16px;
      font-size:8pt;color:rgba(255,255,255,0.65);
    }

    @media print{
      body{margin:0}
      .page{page-break-after:always}
      .page:last-child{page-break-after:auto}
    }
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${title}</title>
<style>${css}</style>
</head>
<body>
${pages.join('\n')}
<script>
  var imgs = document.images;
  function tryPrint(){ setTimeout(function(){ window.print(); }, 600); }
  if(imgs.length === 0){ tryPrint(); }
  else {
    var done = 0;
    Array.from(imgs).forEach(function(img){
      if(img.complete){ done++; if(done===imgs.length) tryPrint(); }
      else { img.onload = img.onerror = function(){ done++; if(done===imgs.length) tryPrint(); }; }
    });
    setTimeout(tryPrint, 3000);
  }
<\/script>
</body>
</html>`;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/** Convert ISO 8601 duration (PT15M, PT1H30M) to readable string */
function parseISO8601Duration(iso) {
  if (!iso) return '';
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/i);
  if (!m) return iso;
  const h = parseInt(m[1] || '0', 10);
  const min = parseInt(m[2] || '0', 10);
  if (h && min) return `${h} hr ${min} mins`;
  if (h) return `${h} hr`;
  if (min) return `${min} mins`;
  return iso;
}

/** Generate a contextual recipe note from ingredients/instructions */
function generateRecipeNote(ingredients = [], instructions = [], title = '') {
  const ingText = ingredients.join(' ').toLowerCase();
  const instText = instructions.join(' ').toLowerCase();
  const allText = ingText + ' ' + instText + ' ' + title.toLowerCase();

  const isBaking = /bak|oven|preheat|flour|butter|sugar|egg|dough|pastry/i.test(allText);
  const hasDairy = /milk|cream|butter|cheese|yogurt/i.test(ingText);
  const hasEgg = /\begg/i.test(ingText);
  const hasSugar = /sugar|honey|maple|syrup/i.test(ingText);
  const hasSpice = /cinnamon|cumin|paprika|turmeric|chili|ginger/i.test(ingText);

  const tips = [];

  if (isBaking) {
    tips.push('Make sure all ingredients are at room temperature before starting for best results.');
    if (hasDairy) tips.push('Swap butter or milk with plant-based alternatives for a dairy-free version.');
    if (hasEgg) tips.push('Flax eggs (1 tbsp ground flax + 3 tbsp water) work as a great egg substitute.');
    if (hasSugar) tips.push('Reduce sugar by 25% without affecting texture, or use coconut sugar for a lower-GI option.');
    tips.push('Store in an airtight container at room temperature for up to 3 days, or freeze for up to 1 month.');
  } else {
    tips.push('Taste and adjust seasoning just before serving for the best flavor.');
    if (hasSpice) tips.push('Toast whole spices in a dry pan before adding for a deeper, more complex flavor.');
    if (hasDairy) tips.push('Use coconut milk or oat cream as a dairy-free alternative.');
    tips.push('Leftovers keep well in the fridge for up to 3 days. Reheat gently over low heat.');
    tips.push('Serve immediately for best texture, or prep components ahead and assemble just before eating.');
  }

  // Keep max 4 tips
  return tips.slice(0, 4).join('\n');
}

function parseHtmlContent(html) {
  if (!html) return {};
  const detected = {};

  try {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // ── Layer 1: JSON-LD Recipe schema ────────────────────────────────────
    const scripts = tmp.querySelectorAll('script[type="application/ld+json"]');
    let schema = null;
    scripts.forEach(s => {
      try {
        const data = JSON.parse(s.textContent);
        const candidates = Array.isArray(data) ? data : [data];
        for (const c of candidates) {
          if (c['@type'] === 'Recipe' || (Array.isArray(c['@type']) && c['@type'].includes('Recipe'))) {
            schema = c; break;
          }
          // @graph pattern
          if (c['@graph']) {
            const r = c['@graph'].find(x => x['@type'] === 'Recipe');
            if (r) { schema = r; break; }
          }
        }
      } catch (_) {}
    });

    if (schema) {
      if (schema.prepTime && !detected.prepTime)
        detected.prepTime = parseISO8601Duration(schema.prepTime);
      if (schema.cookTime && !detected.cookTime)
        detected.cookTime = parseISO8601Duration(schema.cookTime);
      if (!detected.cookTime && schema.totalTime)
        detected.cookTime = parseISO8601Duration(schema.totalTime);
      if (schema.recipeYield && !detected.servings) {
        const y = Array.isArray(schema.recipeYield) ? schema.recipeYield[0] : schema.recipeYield;
        detected.servings = String(y);
      }
      if (schema.nutrition?.calories && !detected.calories) {
        const cal = String(schema.nutrition.calories).trim();
        detected.calories = cal;
      }
      if (schema.recipeIngredient?.length && !detected.ingredients)
        detected.ingredients = schema.recipeIngredient.map(i => String(i).trim()).filter(Boolean);
      if (schema.recipeInstructions?.length && !detected.instructions) {
        detected.instructions = schema.recipeInstructions.map(inst => {
          if (typeof inst === 'string') return inst.trim();
          if (inst.text) return String(inst.text).trim();
          if (inst.itemListElement) return inst.itemListElement.map(x => x.text || '').join(' ');
          return '';
        }).filter(Boolean);
      }
    }

    // ── Layer 2: WordPress recipe plugin CSS selectors ─────────────────────
    const pick = (selectors) => {
      for (const sel of selectors) {
        const el = tmp.querySelector(sel);
        if (el) return el.textContent.trim();
      }
      return null;
    };

    // Helper: extract a clean time string from a container element that may
    // include hours + minutes spans (WPRM pattern). Returns e.g. "1 hr 30 min".
    const extractTime = (containerSel) => {
      const container = tmp.querySelector(containerSel);
      if (!container) return null;
      const text = container.textContent.replace(/\s+/g, ' ').trim();
      return /\d/.test(text) ? text : null;
    };

    if (!detected.prepTime) {
      // Priority 1: WPRM underscore container holds just the value ("15 minutes")
      detected.prepTime =
        extractTime('.wprm-recipe-prep_time-container') ||
        extractTime('.wprm-recipe-prep-time-container .wprm-recipe-block-column-content');

      // Priority 2: other plugins that embed the value directly in one element
      if (!detected.prepTime) {
        const v = pick([
          '.tasty-recipes-prep-time',
          '.recipe-card-prep-time',
          '.easyrecipe-preptime',
          '.prep-time .value',
          '.recipe-prep-time',
        ]);
        if (v && /\d/.test(v)) detected.prepTime = v.replace(/\s+/g, ' ').trim();
      }

      // Priority 3: WPRM hours+minutes separately (fallback)
      if (!detected.prepTime) {
        const hrs  = pick(['.wprm-recipe-prep_time-hours']);
        const mins = pick(['.wprm-recipe-prep_time-minutes', '.wprm-recipe-prep_time']);
        const unit = pick(['.wprm-recipe-prep_time-unit-container']) || 'mins';
        if (hrs && mins) detected.prepTime = `${hrs.trim()} hr ${mins.trim()} min`;
        else if (hrs)    detected.prepTime = `${hrs.trim()} hr`;
        else if (mins)   detected.prepTime = `${mins.trim()} ${unit.trim()}`;
      }
    }

    if (!detected.cookTime) {
      detected.cookTime =
        extractTime('.wprm-recipe-cook_time-container') ||
        extractTime('.wprm-recipe-cook-time-container .wprm-recipe-block-column-content') ||
        extractTime('.wprm-recipe-total_time-container') ||
        extractTime('.wprm-recipe-total-time-container .wprm-recipe-block-column-content');

      if (!detected.cookTime) {
        const v = pick([
          '.tasty-recipes-cook-time',
          '.tasty-recipes-total-time',
          '.recipe-card-cook-time',
          '.easyrecipe-cooktime',
          '.cook-time .value',
          '.recipe-cook-time',
          '.total-time .value',
        ]);
        if (v && /\d/.test(v)) detected.cookTime = v.replace(/\s+/g, ' ').trim();
      }

      if (!detected.cookTime) {
        const hrs  = pick(['.wprm-recipe-cook_time-hours']);
        const mins = pick(['.wprm-recipe-cook_time-minutes', '.wprm-recipe-cook_time']);
        const unit = pick(['.wprm-recipe-cook_time-unit-container']) || 'mins';
        if (hrs && mins) detected.cookTime = `${hrs.trim()} hr ${mins.trim()} min`;
        else if (hrs)    detected.cookTime = `${hrs.trim()} hr`;
        else if (mins)   detected.cookTime = `${mins.trim()} ${unit.trim()}`;
      }

      // Fall back to total time when cook time is absent
      if (!detected.cookTime) {
        const hrs  = pick(['.wprm-recipe-total_time-hours']);
        const mins = pick(['.wprm-recipe-total_time-minutes', '.wprm-recipe-total_time']);
        const unit = pick(['.wprm-recipe-total_time-unit-container']) || 'mins';
        if (hrs && mins) detected.cookTime = `${hrs.trim()} hr ${mins.trim()} min`;
        else if (hrs)    detected.cookTime = `${hrs.trim()} hr`;
        else if (mins)   detected.cookTime = `${mins.trim()} ${unit.trim()}`;
      }
    }

    if (!detected.servings) {
      const v = pick([
        '.wprm-recipe-servings-with-unit',
        '.wprm-recipe-servings-container',
        '.wprm-recipe-servings',
        '.tasty-recipes-yield',
        '.recipe-card-servings',
        '.recipe-yield',
        '.recipe-servings',
      ]);
      if (v) detected.servings = v.replace(/\s+/g, ' ').trim();
    }

    if (!detected.calories) {
      // WPRM nutrition label: value lives in a dedicated nutrition value span
      const calVal = pick([
        '.wprm-nutrition-label-text-nutrition-container-calories .wprm-nutrition-label-text-nutrition-value',
        '.wprm-recipe-calories-container',
        '.wprm-recipe-calories',
        '.tasty-recipes-calories',
        '.recipe-card-calories',
        '[class*="calories"]',
      ]);
      if (calVal && /\d/.test(calVal)) {
        // Grab the unit sibling for WPRM ("kcal") if available
        const calUnit = pick([
          '.wprm-nutrition-label-text-nutrition-container-calories .wprm-nutrition-label-text-nutrition-unit',
        ]);
        detected.calories = calUnit ? `${calVal.trim()} ${calUnit.trim()}` : calVal.trim();
      }
    }
    // ── Layer 2.5: wildcard class/id attribute selectors ──────────────────
    // When a container element is matched, isolate the numeric value by
    // preferring a direct child whose text is ONLY a number (+ optional unit).
    // This avoids returning "15 Prep Time" when the container holds both
    // the value span and the label span.
    const extractValueFromContainer = (el) => {
      if (!el) return null;
      // Prefer a direct child that is purely a numeric/time value
      for (const child of el.children) {
        const ct = child.textContent.trim();
        if (/^\d+(?:\s*(?:min(?:ute)?s?|hours?|hr?s?|kcal|cal))?$/.test(ct)) return ct;
      }
      // Accept the full text only when it's short enough to be a bare value
      const t = el.textContent.replace(/\s+/g, ' ').trim();
      return (/\d/.test(t) && t.length < 20) ? t : null;
    };

    if (!detected.prepTime) {
      const el = tmp.querySelector('[class*="prep_time"],[class*="prep-time"],[id*="prep_time"],[id*="prep-time"]');
      if (el) { const v = extractValueFromContainer(el); if (v) detected.prepTime = v; }
    }
    if (!detected.cookTime) {
      const el = tmp.querySelector(
        '[class*="cook_time"],[class*="cook-time"],[id*="cook_time"],[id*="cook-time"],' +
        '[class*="total_time"],[class*="total-time"],[id*="total_time"],[id*="total-time"]'
      );
      if (el) { const v = extractValueFromContainer(el); if (v) detected.cookTime = v; }
    }
    if (!detected.servings) {
      const el = tmp.querySelector('[class*="servings"],[class*="-yield"],[id*="servings"],[id*="yield"]');
      if (el) { const v = extractValueFromContainer(el); if (v) detected.servings = v; }
    }

    if (!detected.ingredients) {
      const ingContainer = tmp.querySelector(
        '.wprm-recipe-ingredients-container, .tasty-recipes-ingredients, [class*="ingredient-list"], [class*="ingredients-list"]'
      );
      if (ingContainer) {
        const items = ingContainer.querySelectorAll('li');
        if (items.length) detected.ingredients = Array.from(items).map(li => li.textContent.trim()).filter(Boolean);
      }
    }
    if (!detected.instructions) {
      const instContainer = tmp.querySelector(
        '.wprm-recipe-instructions-container, .tasty-recipes-instructions, [class*="instruction-list"], [class*="instructions-list"]'
      );
      if (instContainer) {
        const items = instContainer.querySelectorAll('li');
        if (items.length) detected.instructions = Array.from(items).map(li => li.textContent.trim()).filter(Boolean);
      }
    }

    // ── Layer 3: itemprop microdata attributes ─────────────────────────────
    if (!detected.prepTime) {
      const el = tmp.querySelector('[itemprop="prepTime"]');
      if (el) detected.prepTime = parseISO8601Duration(el.getAttribute('datetime') || el.textContent.trim());
    }
    if (!detected.cookTime) {
      const el = tmp.querySelector('[itemprop="cookTime"]') || tmp.querySelector('[itemprop="totalTime"]');
      if (el) detected.cookTime = parseISO8601Duration(el.getAttribute('datetime') || el.textContent.trim());
    }
    if (!detected.servings) {
      const el = tmp.querySelector('[itemprop="recipeYield"]');
      if (el) detected.servings = el.textContent.trim();
    }
    if (!detected.calories) {
      const el = tmp.querySelector('[itemprop="calories"]');
      if (el) detected.calories = (el.getAttribute('content') || el.textContent).trim();
    }
    if (!detected.ingredients) {
      const els = tmp.querySelectorAll('[itemprop="recipeIngredient"]');
      if (els.length) detected.ingredients = Array.from(els).map(e => e.textContent.trim()).filter(Boolean);
    }
    if (!detected.instructions) {
      const els = tmp.querySelectorAll('[itemprop="recipeInstructions"] li, [itemprop="recipeInstructions"][itemtype]');
      if (els.length) detected.instructions = Array.from(els).map(e => e.textContent.trim()).filter(Boolean);
    }

    // ── Layer 4: DOM heuristics for ingredients/instructions ───────────────
    if (!detected.ingredients) {
      const uls = tmp.querySelectorAll('ul');
      let bestUl = null, bestScore = 0;
      uls.forEach(ul => {
        const items = ul.querySelectorAll('li');
        const text = ul.textContent.toLowerCase();
        let score = 0;
        if (text.includes('cup') || text.includes('tsp') || text.includes('tbsp') || text.includes('oz') || text.includes('gram')) score += 3;
        if (items.length >= 4) score += 2;
        const prev = ul.previousElementSibling;
        if (prev && /ingredient/i.test(prev.textContent)) score += 5;
        if (score > bestScore) { bestScore = score; bestUl = ul; }
      });
      if (bestUl && bestScore >= 3)
        detected.ingredients = Array.from(bestUl.querySelectorAll('li')).map(li => li.textContent.trim()).filter(Boolean);
    }

    if (!detected.instructions) {
      const ols = tmp.querySelectorAll('ol');
      let bestOl = null, bestOlScore = 0;
      ols.forEach(ol => {
        const items = ol.querySelectorAll('li');
        const text = ol.textContent.toLowerCase();
        let score = items.length * 2;
        if (text.includes('preheat') || text.includes('bake') || text.includes('mix') || text.includes('cook') || text.includes('stir')) score += 3;
        const prev = ol.previousElementSibling;
        if (prev && /instruction|direction|step/i.test(prev.textContent)) score += 5;
        if (score > bestOlScore) { bestOlScore = score; bestOl = ol; }
      });
      if (bestOl && bestOlScore >= 4)
        detected.instructions = Array.from(bestOl.querySelectorAll('li')).map(li => li.textContent.trim()).filter(Boolean);
    }

    // ── Layer 4.5: Generic label-value DOM scanner ────────────────────────
    // Finds elements whose text IS exactly a label keyword, then reads the
    // value from siblings WITHIN THE SAME PARENT only.
    //
    // IMPORTANT: never read el.parentElement.nextElementSibling — that crosses
    // into the next recipe card item and returns "15 Cook Time" style strings
    // where a value from the next field is concatenated with its own label.
    if (!detected.prepTime || !detected.cookTime || !detected.servings) {
      const labelMap = [
        { key: 'prepTime', re: /^prep(?:aration)?\s*time\s*:?\s*$/i },
        { key: 'cookTime', re: /^(?:cook(?:ing)?|bak(?:e|ing)|total)\s*time\s*:?\s*$/i },
        { key: 'servings', re: /^(?:servings?|serves?|yield|portions?)\s*:?\s*$/i },
      ];
      const scanEls = tmp.querySelectorAll(
        'span,div,p,li,dt,td,th,strong,b,em,label,h1,h2,h3,h4,h5,h6'
      );
      for (const el of scanEls) {
        const elText = el.textContent.trim();
        if (!elText || elText.length > 60) continue;
        for (const { key, re } of labelMap) {
          if (detected[key] || !re.test(elText)) continue;
          // Only consider immediate siblings within the same parent.
          // Value can come before OR after the label depending on plugin layout.
          const sibling = (node) => {
            if (!node) return null;
            const t = node.textContent.replace(/\s+/g, ' ').trim();
            return (/\d/.test(t) && t.length < 40) ? t : null;
          };
          const val =
            sibling(el.previousElementSibling) ||   // value BEFORE label
            sibling(el.nextElementSibling);           // value AFTER label
          if (val) detected[key] = val;
        }
      }
    }

    // ── Layer 5: Regex fallback on raw text ───────────────────────────────
    const rawText = tmp.textContent;

    if (!detected.prepTime) {
      // Try with explicit unit first; fall back to bare number (default to "mins")
      const prepM =
        rawText.match(/prep(?:aration)?\s*time\s*[:\-–]\s*(\d+\s*(?:min(?:ute)?s?|hours?|hr?s?)(?:\s+\d+\s*(?:min(?:ute)?s?|hours?|hr?s?))?)/i) ||
        rawText.match(/prep(?:aration)?\s*time[:\s]+(\d+\s*(?:min(?:ute)?s?|hours?|hr?s?))/i) ||
        rawText.match(/prep(?:aration)?\s*time[:\s]*(\d+)/i);
      if (prepM) {
        const v = prepM[1].trim();
        detected.prepTime = /min|hr|hour/i.test(v) ? v : `${v} mins`;
      }
    }

    if (!detected.cookTime) {
      const cookM =
        rawText.match(/(?:cook(?:ing)?|bak(?:e|ing)|total)\s*time\s*[:\-–]\s*(\d+\s*(?:min(?:ute)?s?|hours?|hr?s?)(?:\s+\d+\s*(?:min(?:ute)?s?|hours?|hr?s?))?)/i) ||
        rawText.match(/(?:cook(?:ing)?|bak(?:e|ing)|total)\s*time[:\s]+(\d+\s*(?:min(?:ute)?s?|hours?|hr?s?))/i) ||
        rawText.match(/(?:cook(?:ing)?|total)\s*time[:\s]*(\d+)/i);
      if (cookM) {
        const v = cookM[1].trim();
        detected.cookTime = /min|hr|hour/i.test(v) ? v : `${v} mins`;
      }
    }

    if (!detected.servings) {
      const servM =
        rawText.match(/(?:servings?|serves?|yield|portions?)[:\s]+(\d+(?:[–\-]\d+)?(?:\s*\w+)?)/i) ||
        rawText.match(/(?:servings?|serves?|yield)[:\s]*(\d+)/i);
      if (servM) detected.servings = servM[1].trim();
    }

    if (!detected.calories) {
      const calM = rawText.match(/calories?[:\s]+(\d[\d,]*(?:\s*(?:kcal|cal))?)/i);
      if (calM) detected.calories = calM[1].trim();
    }

    // ── Notes — look for a section with "note" heading ─────────────────────
    const headings = tmp.querySelectorAll('h1,h2,h3,h4,h5,h6,strong,b');
    let notesEl = null;
    headings.forEach(h => {
      if (/note/i.test(h.textContent) && !notesEl) notesEl = h;
    });
    if (notesEl) {
      let sibling = notesEl.nextElementSibling;
      const noteParts = [];
      while (sibling && noteParts.length < 5) {
        const tag = sibling.tagName.toLowerCase();
        if (/^h[1-6]$/.test(tag)) break;
        noteParts.push(sibling.textContent.trim());
        sibling = sibling.nextElementSibling;
      }
      if (noteParts.length) detected.notes = noteParts.join('\n');
    }
  } catch (e) {
    // Parsing failed silently
  }

  return detected;
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

const TEMPLATES = [
  { value: 'classic', label: 'Classic Recipe' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'premium', label: 'Premium Etsy Style' },
];

const IMAGE_LAYOUTS = [
  { value: 'full', label: 'Full Width' },
  { value: 'alternating', label: 'Alternating Left/Right' },
  { value: 'compact', label: 'Compact' },
];

const FONT_STYLES = [
  { value: 'serif', label: 'Serif (Classic)' },
  { value: 'sans', label: 'Sans-Serif (Modern)' },
  { value: 'elegant', label: 'Elegant Mix' },
];

const DEFAULT_SETTINGS = {
  template: 'classic',
  brandName: 'EATESS',
  authorName: 'Dana',
  footerText: 'EATESS',
  thankYouMessage: 'Thank you so much for supporting my small business and for grabbing this recipe! I hope this recipe brings a little extra joy (and deliciousness!) to your kitchen. Happy cooking!',
  primaryColor: '#6B4226',
  accentColor: '#F1641E',
  fontStyle: 'sans',
  showNotes: true,
  showPageNumbers: true,
  useFeaturedImage: true,
  insertImages: true,
  imageLayout: 'alternating',
  pageFormat: 'A4',
};

export default function DigitalProducts() {
  // ── Sites & Articles ────────────────────────────────────────────────────
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesError, setArticlesError] = useState(null);
  const [articleSearch, setArticleSearch] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articlePage, setArticlePage] = useState(1);
  const [hasMoreArticles, setHasMoreArticles] = useState(false);
  const [isChangingPage, setIsChangingPage] = useState(false);

  // ── Detected content ────────────────────────────────────────────────────
  const [detected, setDetected] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [isGeneratingData, setIsGeneratingData] = useState(false);

  // ── AI Recipe Tips ───────────────────────────────────────────────────────
  const [recipeTips, setRecipeTips] = useState(null);
  const [isGeneratingTips, setIsGeneratingTips] = useState(false);
  const [tipsError, setTipsError] = useState(null);

  // ── Editable recipe fields ──────────────────────────────────────────────
  const [ingredients, setIngredients] = useState('');
  const [instructions, setInstructions] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');
  const [calories, setCalories] = useState('');
  const [notes, setNotes] = useState('');

  // ── Uploaded images (base64 data URLs; null = use article default) ────────
  const [uploadedMainImage, setUploadedMainImage] = useState(null);
  const [uploadedIngredientsImage, setUploadedIngredientsImage] = useState(null);
  const [uploadedInstructionsImage, setUploadedInstructionsImage] = useState(null);

  // ── Etsy Listing Generator ───────────────────────────────────────────────
  const [etsyTitle, setEtsyTitle] = useState('');
  const [etsyDescription, setEtsyDescription] = useState('');
  const [etsyTags, setEtsyTags] = useState(Array(13).fill(''));
  const [isGeneratingEtsy, setIsGeneratingEtsy] = useState(false);
  const [etsyError, setEtsyError] = useState(null);

  // ── Copy feedback & toast ────────────────────────────────────────────────
  const [copiedKey, setCopiedKey] = useState(null);
  const [toastMsg, setToastMsg] = useState('');
  const copyTimerRef = useRef(null);

  // ── PDF generation lock (prevents double-click / double-dialog) ──────────
  const generateLockRef = useRef(false);

  // ── Settings ─────────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [sitesLoading, setSitesLoading] = useState(true);

  // ── Load Sites ───────────────────────────────────────────────────────────
  useEffect(() => {
    setSitesLoading(true);
    wordpressService.getAllSites()
      .then(data => setSites(data || []))
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, []);

  const ARTICLES_PAGE_SIZE = 12;

  // ── Extract featured image URL with fallback chain ───────────────────────
  // _embedded may be missing from paginated WP responses due to server-side
  // caching. Yoast's og_image is the most reliable fallback since it's always
  // included in the REST response when Yoast SEO is active.
  const getArticleImageUrl = (post) =>
    post._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
    post.jetpack_featured_media_url ||
    post.featured_media_url ||
    post.yoast_head_json?.og_image?.[0]?.url ||
    post.external_featured_image ||
    null;

  // ── Load Articles when site changes ─────────────────────────────────────
  useEffect(() => {
    if (!selectedSiteId) {
      setArticles([]);
      setArticlesError(null);
      setArticlePage(1);
      setHasMoreArticles(false);
      return;
    }
    setArticlesLoading(true);
    setArticlesError(null);
    setSelectedArticle(null);
    setDetected(null);
    setArticlePage(1);
    setHasMoreArticles(false);

    const load = async () => {
      try {
        const site = sites.find(s => s.id === selectedSiteId);
        if (!site?.siteUrl) {
          setArticlesError('Site URL not configured');
          setArticles([]);
          setArticlesLoading(false);
          return;
        }

        const siteUrl = site.siteUrl.replace(/\/$/, '');
        const headers = {};
        if (site.wpUsername && site.wpAppPassword) {
          headers['Authorization'] = 'Basic ' + btoa(`${site.wpUsername}:${site.wpAppPassword}`);
        }

        const res = await fetch(
          `${siteUrl}/wp-json/wp/v2/posts?_embed&per_page=${ARTICLES_PAGE_SIZE}&page=1&orderby=date&order=desc`,
          { headers }
        );
        if (!res.ok) throw new Error(`WordPress API error: ${res.status}`);

        const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10);
        const posts = await res.json();

        const fetched = (posts || []).map(post => ({
          id: String(post.id),
          wpId: post.id,
          site_id: selectedSiteId,
          title: post.title?.rendered || `Post #${post.id}`,
          image_main_url: getArticleImageUrl(post),
          focus_keyword: post.yoast_head_json?.focus_keyphrase || '',
          status: post.status,
          wp_post_url: post.link,
          created_at: post.date,
        }));

        setArticles(fetched);
        setHasMoreArticles(1 < totalPages);
      } catch (e) {
        setArticlesError(e?.message || 'Failed to load articles from WordPress');
        setArticles([]);
      } finally {
        setArticlesLoading(false);
      }
    };
    load();
  }, [selectedSiteId, sites]);

  // ── Navigate to a page ──────────────────────────────────────────────────
  const handleGoToPage = useCallback(async (newPage) => {
    if (isChangingPage || !selectedSiteId || newPage < 1) return;
    setIsChangingPage(true);
    try {
      const site = sites.find(s => s.id === selectedSiteId);
      if (!site?.siteUrl) return;

      const siteUrl = site.siteUrl.replace(/\/$/, '');
      const headers = {};
      if (site.wpUsername && site.wpAppPassword) {
        headers['Authorization'] = 'Basic ' + btoa(`${site.wpUsername}:${site.wpAppPassword}`);
      }

      const res = await fetch(
        `${siteUrl}/wp-json/wp/v2/posts?_embed&per_page=${ARTICLES_PAGE_SIZE}&page=${newPage}&orderby=date&order=desc`,
        { headers }
      );
      if (!res.ok) throw new Error(`WordPress API error: ${res.status}`);

      const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10);
      const posts = await res.json();

      const fetched = (posts || []).map(post => ({
        id: String(post.id),
        wpId: post.id,
        site_id: selectedSiteId,
        title: post.title?.rendered || `Post #${post.id}`,
        image_main_url: getArticleImageUrl(post),
        focus_keyword: post.yoast_head_json?.focus_keyphrase || '',
        status: post.status,
        wp_post_url: post.link,
        created_at: post.date,
      }));

      setArticles(fetched);
      setArticlePage(newPage);
      setHasMoreArticles(newPage < totalPages);
    } catch (_) {
      // silently fail on page change
    } finally {
      setIsChangingPage(false);
    }
  }, [isChangingPage, selectedSiteId, sites]);

  // ── Select Article (highlight only — no extraction) ─────────────────────
  const handleSelectArticle = useCallback((article) => {
    setSelectedArticle(article);
    setLastGenerated(null);
    // Clear any data extracted from a previously selected article
    setDetected(null);
    setWarnings([]);
    setIngredients('');
    setInstructions('');
    setPrepTime('');
    setCookTime('');
    setServings('');
    setCalories('');
    setNotes('');
    setRecipeTips(null);
    setTipsError(null);
  }, []);

  // ── Generate Data (manual trigger) ──────────────────────────────────────
  const handleGenerateData = useCallback(async () => {
    if (!selectedArticle || isGeneratingData) return;
    setIsGeneratingData(true);
    setWarnings([]);
    setDetected(null);

    let content = '';
    // Yoast compiles all recipe plugin schemas (WPRM, Tasty Recipes, etc.)
    // into yoast_head_json.schema['@graph']. This is the most reliable source
    // for prepTime/cookTime/recipeYield because it's always structured JSON-LD,
    // whereas content.rendered may lack the <head> JSON-LD or use unsupported
    // CSS class names.
    let yoastHeadJson = null;

    try {
      // ── Step 1: fetch content + Yoast schema from WP REST API ──────────
      try {
        const site = sites.find(s => s.id === selectedArticle.site_id);
        const siteUrl = site?.siteUrl?.replace(/\/$/, '');
        if (siteUrl && selectedArticle.wpId) {
          const headers = {};
          if (site.wpUsername && site.wpAppPassword) {
            headers['Authorization'] = 'Basic ' + btoa(`${site.wpUsername}:${site.wpAppPassword}`);
          }
          const res = await fetch(
            `${siteUrl}/wp-json/wp/v2/posts/${selectedArticle.wpId}?_fields=content,yoast_head_json`,
            { headers }
          );
          if (res.ok) {
            const post = await res.json();
            content = post.content?.rendered || '';
            yoastHeadJson = post.yoast_head_json || null;
          }
        }
      } catch (_) {}

      // ── Step 2: fall back to slug-based lookup when ID fetch is empty ───
      if (!content && selectedArticle.wp_post_url) {
        try {
          const site = sites.find(s => s.id === selectedArticle.site_id);
          const siteUrl = site?.url || site?.siteUrl || site?.site_url;

          if (siteUrl) {
            // Derive slug from the post URL (last non-empty path segment)
            let slug = '';
            try {
              const urlPath = new URL(selectedArticle.wp_post_url).pathname;
              slug = urlPath.replace(/\/$/, '').split('/').filter(Boolean).pop() || '';
            } catch (_) {
              slug = selectedArticle.wp_post_url
                .replace(/\/$/, '').split('/').filter(Boolean).pop() || '';
            }

            if (slug) {
              const res = await fetch(
                `${siteUrl}/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}&_fields=content,yoast_head_json&per_page=1`
              );
              if (res.ok) {
                const posts = await res.json();
                if (Array.isArray(posts) && posts.length > 0) {
                  content = posts[0].content?.rendered || '';
                  yoastHeadJson = yoastHeadJson || posts[0].yoast_head_json || null;
                }
              }
            }

            // Second chance: query by direct link if slug came up empty
            if (!content) {
              const res2 = await fetch(
                `${siteUrl}/wp-json/wp/v2/posts?link=${encodeURIComponent(selectedArticle.wp_post_url)}&_fields=content,yoast_head_json&per_page=1`
              );
              if (res2.ok) {
                const posts2 = await res2.json();
                if (Array.isArray(posts2) && posts2.length > 0) {
                  content = posts2[0].content?.rendered || '';
                  yoastHeadJson = yoastHeadJson || posts2[0].yoast_head_json || null;
                }
              }
            }
          }
        } catch (_) {}
      }

      // ── Step 3: parse rendered HTML (CSS selectors + regex fallbacks) ────
      const parsed = parseHtmlContent(content);

      // ── Step 3b: supplement from Yoast schema graph ──────────────────────
      // Yoast exposes the same JSON-LD that's in the page <head> via the REST
      // API. This catches prepTime/cookTime/recipeYield that never appear in
      // content.rendered and that CSS selectors may miss due to plugin version
      // differences.
      if (yoastHeadJson) {
        const graph = yoastHeadJson.schema?.['@graph'] || [];
        const recipeNode = graph.find(n =>
          n['@type'] === 'Recipe' ||
          (Array.isArray(n['@type']) && n['@type'].includes('Recipe'))
        );
        if (recipeNode) {
          if (!parsed.prepTime && recipeNode.prepTime)
            parsed.prepTime = parseISO8601Duration(recipeNode.prepTime);
          if (!parsed.cookTime && recipeNode.cookTime)
            parsed.cookTime = parseISO8601Duration(recipeNode.cookTime);
          if (!parsed.cookTime && recipeNode.totalTime)
            parsed.cookTime = parseISO8601Duration(recipeNode.totalTime);
          if (!parsed.servings && recipeNode.recipeYield) {
            const y = Array.isArray(recipeNode.recipeYield)
              ? recipeNode.recipeYield[0]
              : recipeNode.recipeYield;
            parsed.servings = String(y);
          }
          if (!parsed.calories && recipeNode.nutrition?.calories)
            parsed.calories = String(recipeNode.nutrition.calories).trim();
          if (!parsed.ingredients?.length && recipeNode.recipeIngredient?.length)
            parsed.ingredients = recipeNode.recipeIngredient
              .map(i => String(i).trim()).filter(Boolean);
          if (!parsed.instructions?.length && recipeNode.recipeInstructions?.length) {
            parsed.instructions = recipeNode.recipeInstructions.map(inst => {
              if (typeof inst === 'string') return inst.trim();
              if (inst.text) return String(inst.text).trim();
              if (inst.itemListElement)
                return inst.itemListElement.map(x => x.text || '').join(' ');
              return '';
            }).filter(Boolean);
          }
        }
      }

      setDetected(parsed);

      setIngredients((parsed.ingredients || []).join('\n'));
      setInstructions((parsed.instructions || []).join('\n'));
      setPrepTime(parsed.prepTime || '');
      setCookTime(parsed.cookTime || '');
      setServings(parsed.servings || '');
      setCalories(parsed.calories || '');
      // Use notes from article if found; otherwise auto-generate a contextual note
      const autoNote = parsed.notes || generateRecipeNote(
        parsed.ingredients || [],
        parsed.instructions || [],
        selectedArticle?.title || ''
      );
      setNotes(autoNote);

      // ── Step 4: build warning messages ───────────────────────────────────
      const w = [];
      if (!content) {
        w.push('Could not retrieve article content. Please enter recipe data manually.');
      } else {
        if (!parsed.ingredients?.length)
          w.push('Could not auto-detect ingredients — please enter manually.');
        if (!parsed.instructions?.length)
          w.push('Could not auto-detect instructions — please enter manually.');
        if (!parsed.prepTime) w.push('Prep time not detected.');
        if (!parsed.cookTime) w.push('Cook time not detected.');
      }
      setWarnings(w);
    } catch (err) {
      setDetected({});
      setWarnings(['An unexpected error occurred while extracting data. Please try again or enter recipe data manually.']);
    } finally {
      setIsGeneratingData(false);
    }
  }, [selectedArticle, isGeneratingData, sites]);

  // ── Generate AI Recipe Tips ──────────────────────────────────────────────
  const handleGenerateTips = useCallback(async () => {
    if (!selectedArticle || isGeneratingTips) return;
    setIsGeneratingTips(true);
    setTipsError(null);
    try {
      const ingArr  = ingredients.split('\n').map(l => l.trim()).filter(Boolean);
      const instArr = instructions.split('\n').map(l => l.trim()).filter(Boolean);
      const { data, error } = await supabase.functions.invoke('generate-recipe-tips', {
        body: JSON.stringify({
          title: selectedArticle.title,
          ingredients: ingArr,
          instructions: instArr,
          prepTime,
          cookTime,
          servings,
        }),
      });
      if (error) throw error;
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      if (parsed?.tips) {
        setRecipeTips(parsed.tips);
      } else {
        throw new Error('No tips returned from AI');
      }
    } catch (e) {
      setTipsError(e?.message || 'Failed to generate tips');
    } finally {
      setIsGeneratingTips(false);
    }
  }, [selectedArticle, isGeneratingTips, ingredients, instructions, prepTime, cookTime, servings]);

  // ── Generate Etsy Listing ────────────────────────────────────────────────
  const handleGenerateEtsyListing = useCallback(async () => {
    if (!selectedArticle || isGeneratingEtsy) return;
    setIsGeneratingEtsy(true);
    setEtsyError(null);
    try {
      const ingArr = ingredients.split('\n').map(l => l.trim()).filter(Boolean);
      const { data, error } = await supabase.functions.invoke('generate-etsy-listing', {
        body: JSON.stringify({
          title: selectedArticle.title,
          ingredients: ingArr,
          prepTime,
          cookTime,
          servings,
          calories,
        }),
      });
      if (error) throw error;
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      if (parsed?.etsyTitle) {
        setEtsyTitle(parsed.etsyTitle);
        setEtsyDescription(parsed.etsyDescription || '');
        const tags = Array.isArray(parsed.etsyTags) ? parsed.etsyTags : [];
        const padded = [...tags.slice(0, 13)];
        while (padded.length < 13) padded.push('');
        setEtsyTags(padded);
      } else {
        throw new Error('No listing content returned from AI');
      }
    } catch (e) {
      setEtsyError(e?.message || 'Failed to generate Etsy listing');
    } finally {
      setIsGeneratingEtsy(false);
    }
  }, [selectedArticle, isGeneratingEtsy, ingredients, prepTime, cookTime, servings, calories]);

  // ── Setting helper ───────────────────────────────────────────────────────
  const setSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  // ── Generate PDF ─────────────────────────────────────────────────────────
  const handleGenerate = useCallback(() => {
    if (!selectedArticle) return;
    setIsGenerating(true);

    const ingArr = ingredients.split('\n').map(l => l.trim()).filter(Boolean);
    const instArr = instructions.split('\n').map(l => l.trim()).filter(Boolean);

    const html = buildPdfHtml({
      title: selectedArticle.title || 'Recipe',
      coverImage: selectedArticle.image_main_url || '',
      mainImage: uploadedMainImage,
      ingredientsImage: uploadedIngredientsImage,
      instructionsImage: uploadedInstructionsImage,
      brandName: settings.brandName || '',
      authorName: settings.authorName || '',
      footerText: settings.footerText || settings.brandName || '',
      thankYouMessage: settings.thankYouMessage,
      ingredients: ingArr,
      instructions: instArr,
      notes,
      prepTime,
      cookTime,
      servings,
      calories,
      primaryColor: settings.primaryColor,
      accentColor: settings.accentColor,
      fontStyle: settings.fontStyle,
      showNotes: settings.showNotes,
      showPageNumbers: settings.showPageNumbers,
      useFeaturedImage: settings.useFeaturedImage,
      insertImages: settings.insertImages,
      imageLayout: settings.imageLayout,
      pageFormat: settings.pageFormat,
      recipeTips,
    });

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    setLastGenerated({ url });
    setIsGenerating(false);
    return { url };
  }, [selectedArticle, ingredients, instructions, notes, prepTime, cookTime, servings, calories, settings, selectedSiteId, sites, recipeTips, uploadedMainImage, uploadedIngredientsImage, uploadedInstructionsImage]);

  const handleDownload = () => {
    if (!lastGenerated) return;
    const win = window.open(lastGenerated.url, '_blank');
    if (!win) alert('Please allow pop-ups to download the PDF.');
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  // Lock prevents double-click from firing two window.open calls and two print dialogs
  const handleGenerateAndDownload = () => {
    if (generateLockRef.current || !selectedArticle) return;
    generateLockRef.current = true;
    try {
      const result = handleGenerate();
      if (result?.url) {
        const win = window.open(result.url, '_blank');
        if (!win) alert('Please allow pop-ups to download the PDF.');
      }
    } finally {
      generateLockRef.current = false;
    }
  };

  // Copy with 2-second success feedback and toast
  const handleCopy = (text, key) => {
    if (copiedKey === key) return;
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(key);
    setToastMsg('Copied to clipboard!');
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      setCopiedKey(null);
      setToastMsg('');
    }, 2000);
  };

  // Reset all state to initial values
  const handleResetAll = () => {
    if (!window.confirm('Are you sure you want to reset everything? All unsaved changes will be lost.')) return;
    setSelectedArticle(null);
    setLastGenerated(null);
    setDetected(null);
    setWarnings([]);
    setIngredients('');
    setInstructions('');
    setPrepTime('');
    setCookTime('');
    setServings('');
    setCalories('');
    setNotes('');
    setRecipeTips(null);
    setTipsError(null);
    setUploadedMainImage(null);
    setUploadedIngredientsImage(null);
    setUploadedInstructionsImage(null);
    setSettings(DEFAULT_SETTINGS);
    setEtsyTitle('');
    setEtsyDescription('');
    setEtsyTags(Array(13).fill(''));
    setEtsyError(null);
    setCopiedKey(null);
    setToastMsg('');
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  };

  // ── Filtered articles ────────────────────────────────────────────────────
  const filteredArticles = articles.filter(a =>
    !articleSearch || a.title?.toLowerCase().includes(articleSearch.toLowerCase())
  );

  // ── Preview data ─────────────────────────────────────────────────────────
  const ingPreview = ingredients.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 5);
  const instPreview = instructions.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 3);

  // ─── RENDER ──────────────────────────────────────────────────────────────
  return (
    <SidebarProvider>
      <Sidebar />
      <main className="main-content p-6 lg:p-8 bg-[#f7f9fc] min-h-screen">

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Digital Products</h1>
            <p className="text-sm text-gray-500 mt-0.5">Generate styled recipe PDFs from your articles</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleResetAll}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors shadow-sm"
            >
              <RefreshCw size={15} /> Reset All
            </button>
            <button
              onClick={handleGenerateAndDownload}
              disabled={!selectedArticle || isGenerating}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#F1641E] text-white rounded-xl font-semibold text-sm hover:bg-[#d9521a] disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
            >
              {isGenerating
                ? <><Loader2 size={16} className="animate-spin" /> Generating…</>
                : <><FileText size={16} /> Generate PDF</>}
            </button>
          </div>
        </div>

        {/* ── ARTICLE SELECTION CARD ─────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BookOpen size={16} className="text-[#F1641E]" /> Article Selection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Site selector */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Select Website</label>
              <select
                value={selectedSiteId}
                onChange={e => setSelectedSiteId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
              >
                <option value="">{sitesLoading ? 'Loading sites…' : '— Select a website —'}</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>{s.siteName}</option>
                ))}
              </select>
            </div>

            {/* Article search */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Search Articles</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={articleSearch}
                  onChange={e => setArticleSearch(e.target.value)}
                  placeholder="Search by title…"
                  disabled={!selectedSiteId}
                  className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E] disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Article list */}
          {selectedSiteId && (
            <div>
              {articlesLoading ? (
                <div className="flex items-center gap-2 py-6 justify-center text-gray-400 text-sm">
                  <Loader2 size={16} className="animate-spin" /> Loading articles…
                </div>
              ) : articlesError ? (
                <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
                  <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Failed to load articles</p>
                    <p className="text-xs mt-0.5 text-red-500">{articlesError}</p>
                  </div>
                </div>
              ) : filteredArticles.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No articles found for this site.</p>
              ) : (
                <>
                  <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 transition-opacity duration-150 ${isChangingPage ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                    {filteredArticles.map(article => {
                      const isSelected = selectedArticle?.id === article.id;
                      return (
                        <button
                          key={article.id}
                          onClick={() => handleSelectArticle(article)}
                          className={`flex flex-col gap-2 p-3 rounded-xl border text-left transition-all ${
                            isSelected
                              ? 'border-[#F1641E] bg-orange-50 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-full aspect-square rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
                            {article.image_main_url ? (
                              <img
                                src={article.image_main_url}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover"
                                onError={e => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <ImageIcon size={20} className="text-gray-400" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className={`text-xs font-medium leading-tight line-clamp-2 ${isSelected ? 'text-[#F1641E]' : 'text-gray-800'}`}>
                              {article.title}
                            </p>
                            {article.focus_keyword && (
                              <p className="text-[10px] text-gray-400 mt-1 truncate">{article.focus_keyword}</p>
                            )}
                          </div>
                          {isSelected && (
                            <div className="flex justify-end">
                              <CheckCircle size={13} className="text-[#F1641E]" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Pagination controls ── */}
                  <div className="mt-4 flex items-center justify-between">
                    <button
                      onClick={() => handleGoToPage(articlePage - 1)}
                      disabled={articlePage === 1 || isChangingPage}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Previous
                    </button>
                    <span className="text-xs text-gray-400 font-medium">
                      {isChangingPage
                        ? <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading…</span>
                        : `Page ${articlePage}`}
                    </span>
                    <button
                      onClick={() => handleGoToPage(articlePage + 1)}
                      disabled={!hasMoreArticles || isChangingPage}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Detected content summary */}
          {selectedArticle && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles size={14} className="text-[#F1641E] flex-shrink-0" />
                  <span className="text-xs font-semibold text-gray-700 truncate">
                    {detected ? 'Detected Content for: ' : 'Selected: '}
                    <span className="text-[#F1641E]">{selectedArticle.title}</span>
                  </span>
                </div>
                <button
                  onClick={handleGenerateData}
                  disabled={isGeneratingData}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-[#F1641E] text-white rounded-lg text-xs font-semibold hover:bg-[#d9521a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {isGeneratingData
                    ? <><Loader2 size={12} className="animate-spin" /> Extracting…</>
                    : <><Sparkles size={12} /> Generate Data</>}
                </button>
              </div>

              {detected ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {[
                      { label: 'Ingredients', ok: ingredients.trim().length > 0 },
                      { label: 'Instructions', ok: instructions.trim().length > 0 },
                      { label: 'Prep Time', ok: !!prepTime },
                      { label: 'Cook Time', ok: !!cookTime },
                    ].map(item => (
                      <div key={item.label} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${
                        item.ok ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                      }`}>
                        {item.ok ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                        {item.label}
                      </div>
                    ))}
                  </div>
                  {warnings.length > 0 && (
                    <div className="space-y-1">
                      {warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" /> {w}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400 py-1">
                  Click <span className="font-semibold text-gray-500">Generate Data</span> to extract recipe information from this article.
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── MAIN GRID: Settings + Preview ─────────────────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 mb-6">

          {/* LEFT: Settings ──────────────────────────────────────────────── */}
          <div className="xl:col-span-3 space-y-5">

            {/* Recipe Content Fields */}
            {selectedArticle && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ChefHat size={16} className="text-[#F1641E]" /> Recipe Content
                  <span className="text-xs text-gray-400 font-normal">(edit if needed)</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  {[
                    { label: 'Prep Time',  value: prepTime,  set: setPrepTime,  placeholder: 'e.g. 15 mins' },
                    { label: 'Cook Time',  value: cookTime,  set: setCookTime,  placeholder: 'e.g. 20 minutes' },
                    { label: 'Servings',   value: servings,  set: setServings,  placeholder: 'e.g. 12 servings' },
                    { label: 'Calories',   value: calories,  set: setCalories,  placeholder: 'e.g. 320 kcal' },
                  ].map(f => (
                    <div key={f.label}>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">{f.label}</label>
                      <input
                        value={f.value}
                        onChange={e => f.set(e.target.value)}
                        placeholder={f.placeholder}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Ingredients <span className="text-gray-400">(one per line)</span></label>
                    <textarea
                      value={ingredients}
                      onChange={e => setIngredients(e.target.value)}
                      rows={7}
                      placeholder={"¾ cup granulated sugar\n¼ cup vegetable oil\n1 large egg\n…"}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Instructions <span className="text-gray-400">(one step per line)</span></label>
                    <textarea
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                      rows={7}
                      placeholder={"Preheat oven to 350°F.\nIn a bowl, mix the sugar and oil.\nAdd eggs and extracts.\n…"}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes <span className="text-gray-400">(optional)</span></label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Ingredient substitutions, flavor alternatives, storage tips…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                  />
                </div>

                {/* ── AI Recipe Tips ── */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <Sparkles size={14} className="text-[#F1641E]" />
                        Recipe Tips &amp; Extras
                        <span className="text-xs font-normal text-gray-400">(AI-generated, tips page)</span>
                      </h3>
                    </div>
                    <button
                      onClick={handleGenerateTips}
                      disabled={isGeneratingTips || !ingredients.trim()}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F1641E] text-white rounded-lg text-xs font-semibold hover:bg-[#d9521a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGeneratingTips
                        ? <><Loader2 size={12} className="animate-spin" /> Generating…</>
                        : <><Sparkles size={12} /> Generate Tips</>}
                    </button>
                  </div>

                  {tipsError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 mb-3">
                      <AlertCircle size={13} className="flex-shrink-0" />
                      {tipsError}
                    </div>
                  )}

                  {recipeTips ? (
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: '💡 Helpful Tips', key: 'helpfulTips' },
                        { label: '🔄 Substitutions', key: 'substitutions' },
                        { label: '📦 Storage Tips', key: 'storageTips' },
                        { label: '🍽 Serving Suggestions', key: 'servingSuggestions' },
                      ].map(({ label, key }) => (
                        <div key={key} className="bg-gray-50 rounded-xl p-3">
                          <p className="text-[11px] font-semibold text-gray-600 mb-1.5">{label}</p>
                          <ul className="space-y-1">
                            {(recipeTips[key] || []).map((tip, i) => (
                              <li key={i} className="text-xs text-gray-700 flex gap-1.5">
                                <span className="text-[#F1641E] flex-shrink-0 mt-0.5">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">
                      {!ingredients.trim()
                        ? 'Add ingredients first, then generate AI-tailored tips for the PDF.'
                        : 'Click Generate Tips to create recipe-specific tips for the Tips & Extras page.'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* PDF Design Settings */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Palette size={16} className="text-[#F1641E]" /> PDF Design Settings
              </h2>

              {/* Image Management */}
              <div className="mb-5">
                <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                  <ImageIcon size={12} /> Image Management
                </label>
                <div className="space-y-2">
                  {[
                    { label: 'Main Image',          desc: 'Cover page',           state: uploadedMainImage,         setter: setUploadedMainImage },
                    { label: 'Ingredients Image',   desc: 'Ingredients section',  state: uploadedIngredientsImage,  setter: setUploadedIngredientsImage },
                    { label: 'Instructions Image',  desc: 'Instructions section', state: uploadedInstructionsImage, setter: setUploadedInstructionsImage },
                  ].map(img => (
                    <div key={img.label} className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-xl bg-gray-50/50">
                      {/* Thumbnail preview */}
                      <div className="w-14 h-14 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center border border-gray-200">
                        {img.state
                          ? <img src={img.state} className="w-full h-full object-cover" alt={img.label} />
                          : <ImageIcon size={18} className="text-gray-300" />}
                      </div>
                      {/* Info + buttons */}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-700 mb-0.5">{img.label}</div>
                        <div className="text-[10px] text-gray-400 mb-1.5">
                          {img.state ? 'Custom image active' : `Default · ${img.desc}`}
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center gap-1 cursor-pointer bg-orange-50 hover:bg-orange-100 text-[#F1641E] text-[10px] font-medium px-2.5 py-1 rounded-lg border border-orange-200 transition-colors">
                            <Upload size={10} />
                            {img.state ? 'Replace' : 'Upload Image'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = ev => img.setter(ev.target.result);
                                reader.readAsDataURL(file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          {img.state && (
                            <button
                              onClick={() => img.setter(null)}
                              className="text-[10px] text-gray-400 hover:text-red-500 transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brand Settings */}
              <div className="mb-5">
                <label className="block text-xs font-medium text-gray-600 mb-2">Brand Settings</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  {[
                    { label: 'Brand Name', key: 'brandName', placeholder: 'e.g. Eatess' },
                    { label: 'Author Name', key: 'authorName', placeholder: 'e.g. Dana' },
                    { label: 'Footer Text', key: 'footerText', placeholder: 'e.g. Eatess' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-[10px] text-gray-500 mb-1">{f.label}</label>
                      <input
                        value={settings[f.key]}
                        onChange={e => setSetting(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Thank-You Message</label>
                  <textarea
                    value={settings.thankYouMessage}
                    onChange={e => setSetting('thankYouMessage', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                  />
                </div>
              </div>

              {/* Design Options */}
              <div className="mb-5">
                <label className="block text-xs font-medium text-gray-600 mb-2">Design Options</label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Primary Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={settings.primaryColor}
                        onChange={e => setSetting('primaryColor', e.target.value)}
                        className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                      />
                      <input
                        value={settings.primaryColor}
                        onChange={e => setSetting('primaryColor', e.target.value)}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 font-mono focus:outline-none"
                        maxLength={7}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Accent Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={settings.accentColor}
                        onChange={e => setSetting('accentColor', e.target.value)}
                        className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                      />
                      <input
                        value={settings.accentColor}
                        onChange={e => setSetting('accentColor', e.target.value)}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 font-mono focus:outline-none"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>

              </div>

              {/* Image Options */}
              <div className="mb-5">
                <label className="block text-xs font-medium text-gray-600 mb-2">
                  <ImageIcon size={12} className="inline mr-1" /> Image Options
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                  <div
                    onClick={() => setSetting('insertImages', !settings.insertImages)}
                    className={`w-9 h-5 rounded-full transition-colors flex items-center px-0.5 cursor-pointer ${
                      settings.insertImages ? 'bg-[#F1641E]' : 'bg-gray-200'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.insertImages ? 'translate-x-4' : ''}`} />
                  </div>
                  <span className="text-xs text-gray-600">Insert images between sections</span>
                </label>
              </div>

              {/* PDF Format */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">PDF Format</label>
                <div className="grid grid-cols-2 gap-2 max-w-xs">
                  {['A4', 'US Letter'].map(f => (
                    <button
                      key={f}
                      onClick={() => setSetting('pageFormat', f === 'A4' ? 'A4' : 'Letter')}
                      className={`py-2 px-3 rounded-lg border text-xs font-medium transition-all ${
                        settings.pageFormat === (f === 'A4' ? 'A4' : 'Letter')
                          ? 'border-[#F1641E] bg-orange-50 text-[#F1641E]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Live Preview ──────────────────────────────────────────── */}
          <div className="xl:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-6">
              <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Eye size={14} className="text-[#F1641E]" /> Live Preview
              </h2>

              {!selectedArticle ? (
                <div className="text-center py-12 text-gray-400">
                  <FileText size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Select an article to preview</p>
                </div>
              ) : (() => {
                // Build the exact same HTML that will be used for the PDF
                const ingArr = ingredients.split('\n').map(l => l.trim()).filter(Boolean);
                const instArr = instructions.split('\n').map(l => l.trim()).filter(Boolean);
                const rawPdfHtml = buildPdfHtml({
                  title: selectedArticle.title || 'Recipe',
                  coverImage: selectedArticle.image_main_url || '',
                  mainImage: uploadedMainImage,
                  ingredientsImage: uploadedIngredientsImage,
                  instructionsImage: uploadedInstructionsImage,
                  brandName: settings.brandName || '',
                  authorName: settings.authorName || '',
                  footerText: settings.footerText || settings.brandName || '',
                  thankYouMessage: settings.thankYouMessage,
                  ingredients: ingArr,
                  instructions: instArr,
                  notes,
                  prepTime,
                  cookTime,
                  servings,
                  calories,
                  primaryColor: settings.primaryColor,
                  accentColor: settings.accentColor,
                  fontStyle: settings.fontStyle,
                  showNotes: settings.showNotes,
                  showPageNumbers: settings.showPageNumbers,
                  useFeaturedImage: settings.useFeaturedImage,
                  insertImages: settings.insertImages,
                  imageLayout: settings.imageLayout,
                  pageFormat: settings.pageFormat,
                  recipeTips,
                });

                // Inject screen-only overrides: gray background, page shadows, gaps
                // and block the auto-print trigger so it doesn't open the print dialog
                const screenOverrides = `
<style>
  @media screen {
    body { background: #d0d0d0 !important; padding: 24px 0 !important; }
    .page {
      margin: 0 auto 20px !important;
      box-shadow: 0 4px 24px rgba(0,0,0,0.18) !important;
      border-radius: 2px !important;
    }
    .page:last-child { margin-bottom: 0 !important; }
  }
</style>
<script>
  // Block auto-print in preview iframe
  window.onload = null;
  window.print = function() {};
  document.addEventListener('DOMContentLoaded', function() {
    if (window._printTimer) clearTimeout(window._printTimer);
  });
</script>`;
                const previewHtml = rawPdfHtml.replace('</body>', screenOverrides + '</body>');

                // Page dimensions: A4 = 210mm×297mm, Letter = 216mm×279mm (at 96 dpi, 1mm ≈ 3.7795px)
                const isLetter = settings.pageFormat !== 'A4';
                const PAGE_W_PX = isLetter ? 816 : 794;  // 216mm vs 210mm
                const PAGE_H_PX = isLetter ? 1056 : 1123; // 279mm vs 297mm

                // Count total pages (mirrors buildPdfHtml logic)
                const instChunkCount = Math.max(1, Math.ceil(instArr.length / 3));
                const hasNotesPg = settings.showNotes && notes.trim();
                const previewHasTips = recipeTips && (
                  (recipeTips.helpfulTips?.length || 0) +
                  (recipeTips.substitutions?.length || 0) +
                  (recipeTips.storageTips?.length || 0) +
                  (recipeTips.servingSuggestions?.length || 0)
                ) > 0;
                const previewTipChars = previewHasTips
                  ? [...(recipeTips.helpfulTips||[]), ...(recipeTips.substitutions||[]), ...(recipeTips.storageTips||[]), ...(recipeTips.servingSuggestions||[])].join('').length
                  : 0;
                const previewTipItems = previewHasTips
                  ? (recipeTips.helpfulTips?.length||0) + (recipeTips.substitutions?.length||0) + (recipeTips.storageTips?.length||0) + (recipeTips.servingSuggestions?.length||0)
                  : 0;
                const previewTipsPageCount = previewHasTips
                  ? ((previewTipItems > 10 || previewTipChars > 700) ? 2 : 1)
                  : 0;
                const totalPages = 1 + 1 + instChunkCount + previewTipsPageCount + (hasNotesPg ? 1 : 0) + 1;

                // iframe is full PDF width; height = all pages + gaps + body padding
                const PAGE_GAP = 20;
                const BODY_PAD = 48; // 24px top + 24px bottom
                const iframeW = PAGE_W_PX;
                const iframeH = totalPages * PAGE_H_PX + (totalPages - 1) * PAGE_GAP + BODY_PAD;

                // Scale to fit the preview panel (~390px wide)
                const PANEL_W = 390;
                const SCALE = PANEL_W / iframeW;
                const scaledH = Math.round(iframeH * SCALE);

                return (
                  <div>
                    <p className="text-[10px] text-gray-400 mb-3 text-center">{totalPages} pages · exact PDF preview</p>
                    {/* Scrollable outer container */}
                    <div
                      className="overflow-y-auto rounded-lg"
                      style={{ maxHeight: '640px', background: '#c8c8c8', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                      {/* Sized container that matches scaled iframe dimensions */}
                      <div style={{ width: PANEL_W + 'px', height: scaledH + 'px', position: 'relative', flexShrink: 0 }}>
                        <iframe
                          srcDoc={previewHtml}
                          title="PDF Preview"
                          scrolling="no"
                          style={{
                            width: iframeW + 'px',
                            height: iframeH + 'px',
                            border: 'none',
                            display: 'block',
                            transform: `scale(${SCALE})`,
                            transformOrigin: 'top left',
                            pointerEvents: 'none',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ── ACTIONS BAR ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleGenerateAndDownload}
              disabled={!selectedArticle || isGenerating}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#F1641E] text-white rounded-xl font-semibold text-sm hover:bg-[#d9521a] disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
            >
              {isGenerating
                ? <><Loader2 size={15} className="animate-spin" /> Generating…</>
                : <><FileText size={15} /> Generate PDF</>}
            </button>
            <button
              onClick={handleResetAll}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors shadow-sm"
            >
              <RefreshCw size={15} /> Reset All
            </button>

            {lastGenerated && (
              <>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Download size={15} /> Download PDF
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors shadow-sm"
                >
                  <RefreshCw size={15} /> Regenerate
                </button>
              </>
            )}
          </div>

          {lastGenerated && (
            <p className="text-xs text-gray-400 mt-2">
              PDF is ready. Click "Download PDF" to open the print dialog — use your browser's "Save as PDF" option.
            </p>
          )}
        </div>

        {/* ── ETSY LISTING GENERATOR ──────────────────────────────────────────── */}
        {selectedArticle && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Tag size={16} className="text-[#F1641E]" />
                Etsy Listing Generator
              </h2>
              <button
                onClick={handleGenerateEtsyListing}
                disabled={isGeneratingEtsy}
                className="flex items-center gap-2 px-4 py-2 bg-[#F1641E] text-white rounded-xl font-semibold text-sm hover:bg-[#d9521a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {isGeneratingEtsy
                  ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
                  : <><Sparkles size={14} /> Generate Etsy Listing</>}
              </button>
            </div>

            {etsyError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 mb-5">
                <AlertCircle size={14} className="flex-shrink-0" /> {etsyError}
              </div>
            )}

            {/* Etsy Title */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600">
                  Etsy Title
                  <span className="text-gray-400 font-normal ml-1">({etsyTitle.length}/140 characters)</span>
                </label>
                {etsyTitle && (
                  <button
                    onClick={() => handleCopy(etsyTitle, 'etsy-title')}
                    disabled={copiedKey === 'etsy-title'}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2.5 text-[14px] font-medium transition-all ${copiedKey === 'etsy-title' ? 'border-green-600 text-green-600' : 'border-[#F1641E] text-[#F1641E] hover:bg-[#F1641E]/10'}`}
                  >
                    {copiedKey === 'etsy-title' ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy Title</>}
                  </button>
                )}
              </div>
              <input
                value={etsyTitle}
                onChange={e => setEtsyTitle(e.target.value.slice(0, 140))}
                maxLength={140}
                placeholder="Your SEO-optimized Etsy title will appear here after generating…"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
              />
            </div>

            {/* Etsy Description */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600">Etsy Description</label>
                {etsyDescription && (
                  <button
                    onClick={() => handleCopy(etsyDescription, 'etsy-desc')}
                    disabled={copiedKey === 'etsy-desc'}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2.5 text-[14px] font-medium transition-all ${copiedKey === 'etsy-desc' ? 'border-green-600 text-green-600' : 'border-[#F1641E] text-[#F1641E] hover:bg-[#F1641E]/10'}`}
                  >
                    {copiedKey === 'etsy-desc' ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy Description</>}
                  </button>
                )}
              </div>
              <textarea
                value={etsyDescription}
                onChange={e => setEtsyDescription(e.target.value)}
                rows={9}
                placeholder="Your Etsy product description will appear here after generating…"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
              />
            </div>

            {/* Etsy Tags */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600">
                  Etsy Tags
                  <span className="text-gray-400 font-normal ml-1">(13 tags)</span>
                </label>
                {etsyTags.some(t => t.trim()) && (
                  <button
                    onClick={() => handleCopy(etsyTags.filter(t => t.trim()).join(', '), 'etsy-tags')}
                    disabled={copiedKey === 'etsy-tags'}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2.5 text-[14px] font-medium transition-all ${copiedKey === 'etsy-tags' ? 'border-green-600 text-green-600' : 'border-[#F1641E] text-[#F1641E] hover:bg-[#F1641E]/10'}`}
                  >
                    {copiedKey === 'etsy-tags' ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy All Tags</>}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {etsyTags.map((tag, i) => (
                  <div key={i} className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[9px] text-gray-300 font-medium select-none pointer-events-none">{i + 1}</span>
                    <input
                      value={tag}
                      onChange={e => {
                        const next = [...etsyTags];
                        next[i] = e.target.value;
                        setEtsyTags(next);
                      }}
                      maxLength={20}
                      placeholder={`tag ${i + 1}`}
                      className="w-full border border-gray-200 rounded-lg pl-6 pr-2.5 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#F1641E]/30 focus:border-[#F1641E]"
                    />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Each tag is editable. Max 20 characters per tag.</p>
            </div>
          </div>
        )}

      </main>

      {/* ── TOAST NOTIFICATION ──────────────────────────────────────────────── */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg pointer-events-none">
          <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
          {toastMsg}
        </div>
      )}
    </SidebarProvider>
  );
}
